<template>
    <Card :title="title">
      <!-- Summary Mode -->
      <div v-if="summaryOnly">
        <div class="summary">
          <p><strong>Users:</strong> {{ (report.users && report.users.length) || 0 }}</p>
          <p><strong>Teams:</strong> {{ (report.contracts && report.contracts.length) || 0 }}</p>
          <p><strong>Messages:</strong> {{ report.messages.length }}</p>
          <p><strong>Average Sentiment:</strong> {{ report.sentiment.toFixed(2) }}</p>
          <p><strong>MTTA:</strong> {{ formatTime(report.mtta) }}</p>
        </div>
      </div>

      <!-- Full Details Mode -->
      <div v-else class="row">
        <!-- Users -->
        <transition name="fade" appear>
          <div v-if="report.users && report.users.length">
            <div class="card">
              <h4>
                Humans
                <span class="user-count">{{ report.users.length }}</span>
              </h4>
              <dl>
                <template v-for="user in report.users">
                  <dt :key="'user-' + user.user">{{ user.user }}</dt>
                  <dd :key="'count-' + user.user">{{ user.count }}</dd>
                </template>
              </dl>
            </div>
          </div>
        </transition>

        <!-- Contracts / Teams -->
        <transition name="fade" appear>
          <div v-if="report.contracts && report.contracts.length">
            <div class="card">
              <h4 class="teams">
                Teams
                <span class="contract-count">{{ report.contracts.length }}</span>
              </h4>
              <dl>
                <template v-for="contract in report.contracts">
                  <dt
                    :key="'contract-' + contract.contract"
                    @mouseover="hoverContract = contract.contract"
                    @mouseout="hoverContract = null"
                    @click="updateContract(contract.contract)"
                    class="contracts"
                    :class="{
                      'hover-contract': hoverContract === contract.contract,
                      'inactive-contract': hoverContract && hoverContract !== contract.contract
                    }"
                  >
                    {{ contract.contract }}
                  </dt>
                  <dd :key="'count-' + contract.contract">{{ contract.count }}</dd>
                </template>
              </dl>
            </div>
          </div>
        </transition>

        <!-- Responsiveness & Sentiment -->
        <transition name="fade" appear>
          <div v-if="report.messages.length">
            <div
              class="card"
              :class="{
                positive: report.sentiment > sentimentThreshold,
                negative: report.sentiment < sentimentThreshold
              }"
            >
              <h4>Responsiveness</h4>
              <dl>
                <dt>MTTA</dt>
                <dd>{{ formatTime(report.mtta) }}</dd>
              </dl>

              <span class="spacer"></span>

              <h4>Sentiment</h4>
              <dl>
                <dt>Score</dt>
                <dd>{{ report.sentiment.toFixed(2) }}</dd>
              </dl>

              <span class="spacer"></span>

              <h4 v-if="report.topics.length">Words</h4>
              <dl>
                <template v-for="topic in report.topics">
                  <dt :key="'topic-' + topic.topic">{{ topic.topic }}</dt>
                  <dd :key="'count-' + topic.topic">{{ topic.count }}</dd>
                </template>
              </dl>
            </div>
          </div>
        </transition>
      </div>

      <!-- Message List (only in full mode) -->
      <div v-if="!summaryOnly" class="column">
        <transition-group name="list" appear>
          <div
            v-for="message in report.messages"
            :key="message.timestamp"
            class="card"
            :class="{
              positive: sentimentPositive(message.sentiment),
              negative: sentimentNegative(message.sentiment),
              neutral: sentimentNeutral(message.sentiment)
            }"
          >
            <h5>
              {{ message.user }}
              <span class="timestamp">{{ formatTimestamp(message.timestamp, period) }}</span>
            </h5>
            <p>{{ message.message }}</p>
            <div v-if="message.ack_ts > 0" class="ack">
              {{ calcAckTime(message.timestamp, message.ack_ts) }}
            </div>
          </div>
        </transition-group>
      </div>

      <router-link
      :to="`/${id}/base2bot`"
      class="inline-flex items-center px-5 py-3 bg-white text-indigo-600 font-semibold border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 transition duration-200"
    >
      View Detailed Slack Report
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="ml-2 h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </router-link>

    </Card>
  </template>

<script>
import Card from '../Card.vue'
import HTTP from '@/common/http'
import { mapGetters } from 'vuex'

export default {
  name: 'SlackCard',

  components: { Card },

  props: {
    title: { type: String, default: 'Slack' },
    id: { type: [String, Number], required: true },
    summaryOnly: { type: Boolean, default: false }
  },

  data () {
    return {
      metrics: [],
      report: { month: null, messages: [], topics: [], users: [], contracts: [], sentiment: 0, mtta: 0 },
      date: null,
      period: 'week',
      sentimentThreshold: 80,
      hoverContract: null,
      loading: false
    }
  },

  computed: { ...mapGetters(['activeType']) },

  created () { this.getMetrics(true) },

  watch: {
    id () { this.resetReport(); this.getMetrics(false); this.getMessages() },
    period () { this.resetReport(); this.getMetrics(true) },
    date () { this.resetReport(); this.getMessages() }
  },

  methods: {
    resetReport () {
      this.report = { month: null, messages: [], topics: [], users: [], contracts: [], sentiment: 0, mtta: 0 }
      this.hoverContract = null
    },
    updateMetric (event) {
      if (event.eventType === 'date') this.date = event.data.date
      if (event.eventType === 'period') this.period = event.data.period
    },
    sentimentPositive (s) { return s === 'POSITIVE' },
    sentimentNegative (s) { return s === 'NEGATIVE' },
    sentimentNeutral (s) { return s === 'NEUTRAL' },
    formatTimestamp (ts, period) {
      const d = new Date(ts * 1000)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const m = String(d.getMinutes()).padStart(2, '0')
      return period === 'week'
        ? `${days[d.getDay()]} ${d.getHours()}:${m}`
        : `${months[d.getMonth()]} ${d.getDate()}, ${d.getHours()}:${m}`
    },
    calcAckTime (ts, ack) { return ack > 0 ? this.formatTime(ack - ts) : '' },
    formatTime (sec) {
      if (sec <= 0) return ''
      const mins = Math.floor(sec / 60)
      const hrs = Math.floor(mins / 60)
      return hrs ? `${hrs}h` : mins ? `${mins}m` : `${sec}s`
    },
    getMetrics (setDate) {
      this.loading = true
      HTTP.get(`/base2bot/metrics/${this.activeType}/${this.id}/${this.period}`)
        .then(({ data }) => { this.metrics = data.metrics; if (setDate) this.date = data.metrics[11].date })
        .catch(err => { const msg = (err.response && err.response.data && err.response.data.error) || err.message; this.flashMessage.error({ message: `Failed to get metrics: ${msg}` }) })
        .finally(() => (this.loading = false))
    },
    getMessages () {
      this.loading = true
      HTTP.get(`/base2bot/messages/${this.activeType}/${this.id}/${this.period}/${this.date}`)
        .then(({ data }) => {
          this.report = data
        })
        .catch(err => { const msg = (err.response && err.response.data && err.response.data.error) || err.message; this.flashMessage.error({ message: `Failed to get messages: ${msg}` }) })
        .finally(() => (this.loading = false))
    },
    updateContract (c) { this.$store.dispatch('SET_ACTIVE', c) }
  }
}
</script>

  <style scoped>
  .summary p { margin: 0.5em 0; }
  /* existing styles… */
  </style>



// WEBPACK FOOTER //
// src/components/Cards/InfoCards/SlackCard.vue